import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export type Place = {
  id: string;
  group_id: string;
  name: string;
  lat: number;
  lng: number;
  created_by: string;
  created_at: string;
};

export function usePlaces(groupId: string | null) {
  const [places, setPlaces] = useState<Place[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!groupId) {
      setPlaces([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('places').select('*').eq('group_id', groupId);
      if (cancelled) return;
      if (error) setError(error.message);
      else setPlaces((data ?? []) as Place[]);
    })();

    const channel = supabase
      .channel(`places-${groupId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'places', filter: `group_id=eq.${groupId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            const old = payload.old as Place;
            setPlaces((prev) => prev.filter((p) => p.id !== old.id));
          } else {
            const row = payload.new as Place;
            setPlaces((prev) => {
              const without = prev.filter((p) => p.id !== row.id);
              return [...without, row];
            });
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [groupId]);

  return { places, error };
}

export async function savePlace(
  groupId: string,
  userId: string,
  name: string,
  lat: number,
  lng: number
): Promise<Place> {
  const trimmed = name.trim().slice(0, 40);
  const { data, error } = await supabase
    .from('places')
    .upsert(
      { group_id: groupId, created_by: userId, name: trimmed, lat, lng },
      { onConflict: 'group_id,name_lower' }
    )
    .select()
    .single();
  if (error) throw error;
  return data as Place;
}

export function findPlaceByName(places: Place[], token: string): Place | null {
  const t = token.trim().toLowerCase();
  if (!t) return null;
  return places.find((p) => p.name.toLowerCase() === t) ?? null;
}

type NearestPlace = { place: Place; distanceM: number };
function nearestPlaceWithDistance(places: Place[], lat: number, lng: number): NearestPlace | null {
  if (places.length === 0) return null;
  let best: Place | null = null;
  let bestDist = Infinity;
  for (const p of places) {
    const dx = (p.lat - lat) * 111_139;
    const dy = (p.lng - lng) * 111_139 * Math.cos((lat * Math.PI) / 180);
    const d = Math.hypot(dx, dy);
    if (d < bestDist) {
      bestDist = d;
      best = p;
    }
  }
  if (!best) return null;
  return { place: best, distanceM: bestDist };
}

export function nearbyPlace(places: Place[], lat: number, lng: number): Place | null {
  const n = nearestPlaceWithDistance(places, lat, lng);
  return n && n.distanceM < 500 ? n.place : null;
}
